import React from 'react'
import { NumericFormat } from 'react-number-format'
import { Box, Text, Stack, Group, Button, createStyles } from '@mantine/core'
import { ChainIcon } from '@/page-components/shared'
import { useBurn } from './BurnProvider'
import { FIXED_STRD_SUPPLY, formatStrd, getNumberInput } from './general'

export const BurnInputs: React.FC = () => {
  const {
    strdAmount,
    setStrdAmount,
    burnAmount,
    setBurnAmount,
    networkBurnAmount,
    setNetworkBurnAmount,
    handleBurnPreset,
    results
  } = useBurn()

  const PRESET_LABELS: Record<number, string> = {
    0: 'low',
    25: 'mid',
    50: 'high',
    100: 'max'
  }

  const holdingsNum = getNumberInput(strdAmount)
  const burnNum = getNumberInput(burnAmount)
  const burnPercent = holdingsNum > 0 ? Math.min(100, Math.max(0, Math.round((burnNum / holdingsNum) * 100))) : 0

  return (
    <Stack spacing="md">
      <Box>
        <Text size="sm" sx={(t) => ({ color: t.colors.gray[7], marginBottom: 8 })}>
          Your STRD holdings
        </Text>

        <BurnInput autoFocus placeholder="250,000" value={strdAmount} onChange={setStrdAmount} />
      </Box>

      <Box>
        <Group position="apart" align="center" mb={8}>
          <Text size="sm" sx={(t) => ({ color: t.colors.gray[7] })}>
            Your Burn
          </Text>

          <Group spacing={2}>
            {[0, 25, 50, 100].map((pct) => {
              const holdings = getNumberInput(strdAmount)
              const burn = getNumberInput(burnAmount)
              const presetNum = Math.round((holdings * pct) / 100)
              const isActive = Math.round(burn) === presetNum && presetNum > 0

              return (
                <Button
                  key={pct}
                  size="xs"
                  compact
                  onClick={() => handleBurnPreset(pct)}
                  sx={(t) => ({
                    fontFamily: t.other.monoFontFamily,
                    fontWeight: 400,
                    fontSize: 11,
                    height: 24,
                    padding: '0 8px',
                    background: isActive ? t.colors.pink[6] : t.colors.gray[6],
                    color: t.white,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    '&:first-of-type': {
                      borderTopLeftRadius: t.radius.md,
                      borderBottomLeftRadius: t.radius.md
                    },
                    '&:last-of-type': {
                      borderTopRightRadius: t.radius.md,
                      borderBottomRightRadius: t.radius.md
                    },
                    '&:hover': {
                      background: t.colors.pink[6]
                    }
                  })}>
                  {PRESET_LABELS[pct]}
                </Button>
              )
            })}
          </Group>
        </Group>
        <BurnInput placeholder="125,000" value={burnAmount} onChange={setBurnAmount} />
        <Text size="xs" sx={(t) => ({ color: t.colors.gray[6], marginTop: 4 })}>
          Max allowed is your current holdings ({formatStrd(getNumberInput(strdAmount))}). You're burning {burnPercent}
          %.
        </Text>
      </Box>

      <Box>
        <Text size="sm" sx={(t) => ({ color: t.colors.gray[7], marginBottom: 8 })}>
          Estimated Network Burn
        </Text>
        <BurnInput placeholder="70,000,000" value={networkBurnAmount} onChange={setNetworkBurnAmount} />
        <Text size="xs" sx={(t) => ({ color: t.colors.gray[6], marginTop: 4 })}>
          Fixed STRD supply = 100M. Post-burn supply will be {formatStrd(results.postBurnSupply)}.
        </Text>
      </Box>
    </Stack>
  )
}

interface BurnInputProps {
  placeholder: string
  value: string
  onChange: (value: string) => void
  autoFocus?: boolean
}

const useStyles = createStyles((t) => ({
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 16,
    fontWeight: 500,
    fontFamily: t.fontFamily,
    color: t.colors.gray[9],
    backgroundColor: 'transparent',
    textAlign: 'right',
    '&::placeholder': { color: t.colors.gray[6] }
  }
}))

const BurnInput: React.FC<BurnInputProps> = ({ placeholder, value, onChange, autoFocus }) => {
  const { classes } = useStyles()
  return (
    <Box
      component="label"
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        border: `1px solid ${t.colors.gray[4]}`,
        borderRadius: t.radius.sm,
        backgroundColor: t.white,
        transition: 'border-color 0.15s ease',
        cursor: 'text',
        '&:focus-within': {
          borderColor: t.colors.pink[6],
          outline: `1px solid ${t.colors.pink[6]}`
        }
      })}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, userSelect: 'none' }}>
        <ChainIcon denom="STRD" size={24} />
        <Text size="sm" weight={600} sx={(t) => ({ color: t.colors.gray[9], userSelect: 'none' })}>
          STRD
        </Text>
      </Box>

      <NumericFormat
        placeholder={placeholder}
        value={value}
        onValueChange={(values) => onChange(values.value)}
        autoFocus={autoFocus}
        className={classes.input}
        thousandSeparator
        isAllowed={(values) => (values.floatValue ? values.floatValue <= FIXED_STRD_SUPPLY : true)}
      />
    </Box>
  )
}
